import {
  Target, Zap, FileText, Clock, Briefcase, Shield,
  Wand2, BarChart3, Eye, GripVertical, Download, Save,
  Link2, Copy, Layers, FileCheck
} from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    icon: Zap,
    title: "Speed-First Design",
    description: "Built for volume applicants. Copy-paste your content, toggle sections, and export in seconds. Apply to 10+ jobs in one sitting.",
    accent: true,
  },
  {
    icon: Link2,
    title: "Saved Links Library",
    description: "Store your LinkedIn, GitHub, portfolio, and project links once. Select them from a dropdown instead of digging through bookmarks.",
  },
  {
    icon: Eye,
    title: "Toggle Sections On/Off",
    description: "Applying to different roles? Hide or show sections instantly. No rewriting, just toggle and download a new version.",
  },
  {
    icon: Download,
    title: "Smart File Naming",
    description: "Set your target role, company, and name once. Every PDF and DOCX export is auto-named for easy organization.",
  },
  {
    icon: Copy,
    title: "Copy-Paste Import",
    description: "Already have a resume? Paste the text directly. Our parser structures it instantly so you can start editing right away.",
  },
  {
    icon: GripVertical,
    title: "Drag & Reorder",
    description: "Rearrange resume sections by dragging tabs. Put your strongest sections first for each application.",
  },
  {
    icon: Layers,
    title: "Multiple Templates",
    description: "Switch between Modern and Harvard styles with one click. Each template is ATS-optimized and professionally formatted.",
  },
  {
    icon: Save,
    title: "Save Multiple Versions",
    description: "Save different resume versions for different roles. Switch between them when applying to different types of positions.",
  },
  {
    icon: Target,
    title: "ATS Optimization",
    description: "Real-time ATS scoring ensures your resume passes through Applicant Tracking Systems with the right keywords.",
  },
  {
    icon: Wand2,
    title: "AI-Powered Tailoring",
    description: "Paste a job description and let AI rewrite your bullet points with action verbs and quantified achievements.",
  },
  {
    icon: Briefcase,
    title: "Application Tracker",
    description: "Keep all your job applications organized in one dashboard. Track status, add notes, and manage follow-ups.",
  },
  {
    icon: FileText,
    title: "PDF & DOCX Export",
    description: "Download in ATS-friendly PDF or Word format. Both are professionally formatted and ready to submit.",
  },
];

const FeaturesGrid = () => {
  return (
    <section className="py-20 relative">
      <div className="absolute inset-0 -z-10 gradient-surface" />

      <div className="w-full px-6 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            Built for Speed
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Features That{" "}
            <span className="text-gradient">Save You Hours</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Every feature is designed to eliminate repetitive work so you can focus on what matters: landing interviews.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 max-w-[1400px] mx-auto">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className={`group relative rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1 ${
                feature.accent
                  ? "bg-primary/5 border-primary/20 hover:border-primary/40 hover:shadow-glow"
                  : "bg-card border-border/50 hover:border-primary/30 hover:shadow-lg"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 ${
                  feature.accent
                    ? "gradient-primary shadow-lg"
                    : "bg-primary/10"
                }`}
              >
                <feature.icon
                  className={`w-6 h-6 ${
                    feature.accent ? "text-primary-foreground" : "text-primary"
                  }`}
                />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2 text-foreground">
                {feature.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
